"use client";

import type { AuthChangeEvent, Session, SupabaseClient, User } from "@supabase/supabase-js";
import { useRouter } from "next/navigation";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { resetUser } from "@/lib/services/posthog";
import { createClient } from "@/lib/supabase/client";
import type { Database } from "@/lib/types/supabase";
import { useClearUser, useSetCurrentUser } from "@/stores";

export type DeletionRecoveryState = {
	scheduledDate: string;
	userId: string;
} | null;

type SupabaseContext = {
	supabase: SupabaseClient<Database>;
	user: User | null;
	session: Session | null;
	isLoading: boolean;
	signOut: () => Promise<void>;
	deletionRecovery: DeletionRecoveryState;
	handleDeletionRecovery: (restore: boolean) => Promise<void>;
};

// Use null as default to avoid creating Supabase client at module load time
// This prevents build-time errors when env vars aren't available yet
const SupabaseContext = createContext<SupabaseContext | null>(null);

export function SupabaseProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [session, setSession] = useState<Session | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [deletionRecovery, setDeletionRecovery] = useState<DeletionRecoveryState>(null);
	const router = useRouter();

	// Zustand store actions for global user state
	const setCurrentUser = useSetCurrentUser();
	const clearUser = useClearUser();

	const [supabase] = useState<SupabaseClient<Database>>(() => {
		return createClient() as unknown as SupabaseClient<Database>;
	});

	// Use a ref to track if we've already initialized
	const hasInitialized = useRef(false);

	useEffect(() => {
		// Skip if already initialized
		if (hasInitialized.current) {
			return;
		}

		const getSession = async () => {
			setIsLoading(true);
			try {
				const {
					data: { session },
					error,
				} = await supabase.auth.getSession();

				if (error) {
					console.error("[GLOBAL PROVIDER] Error getting session:", error);
				} else {
					setSession(session);
					setUser(session?.user ?? null);

					// Sync with global user store
					if (session?.user) {
						setCurrentUser({
							id: session.user.id,
							email: session.user.email,
							// Add other user metadata as needed
						});
					} else {
						clearUser();
					}
				}
			} catch (err) {
				console.error("[GLOBAL PROVIDER] Exception getting session:", err);
			} finally {
				setIsLoading(false);
				hasInitialized.current = true;
			}
		};

		getSession();

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
			setSession(session);
			setUser(session?.user ?? null);
			setIsLoading(false);

			// Sync with global user store on auth state changes
			if (session?.user) {
				setCurrentUser({
					id: session.user.id,
					email: session.user.email,
					// Add other user metadata as needed
				});
			} else {
				clearUser();
				// Reset PostHog user identification on logout
				try {
					resetUser();
				} catch (error) {
					console.error("[PostHog] Error resetting user:", error);
				}
			}

			// Check if user has a pending account deletion (show confirmation modal)
			if (event === "SIGNED_IN" && session?.user) {
				try {
					const { data: profile } = await supabase
						.from("profiles")
						.select("deleted_at, scheduled_deletion_at, pre_deletion_data")
						.eq("id", session.user.id)
						.single();

					if (
						profile?.deleted_at &&
						profile?.pre_deletion_data &&
						profile?.scheduled_deletion_at &&
						new Date(profile.scheduled_deletion_at) > new Date()
					) {
						// Don't auto-restore — show confirmation modal instead
						setDeletionRecovery({
							scheduledDate: profile.scheduled_deletion_at,
							userId: session.user.id,
						});
					}
				} catch (err) {
					console.error("Failed to check deletion status:", err);
				}
			}

			if (event === "SIGNED_OUT") {
				setDeletionRecovery(null);
				// Navigate to festivals page (which supports non-logged in state)
				// Cache will be cleared by QueryProvider's auth listener
				router.push("/festivals");
			}
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [supabase, setCurrentUser, clearUser, router]);

	const signOut = useCallback(async () => {
		try {
			const { error } = await supabase.auth.signOut();
			if (error) {
				console.error("[GLOBAL PROVIDER] Error signing out:", error);
			}
		} catch (error) {
			console.error("[GLOBAL PROVIDER] Exception signing out:", error);
		} finally {
			// Clear local state after sign-out attempt (success or failure)
			setSession(null);
			setUser(null);
			clearUser();
			setDeletionRecovery(null);

			// Reset PostHog user identification
			try {
				resetUser();
			} catch (posthogError) {
				console.error("[PostHog] Error resetting user on signout:", posthogError);
			}

			// Navigation is handled by onAuthStateChange SIGNED_OUT listener
		}
	}, [supabase, clearUser]);

	/**
	 * Handle the user's choice when they sign in while their account is pending deletion.
	 * @param restore - true to cancel deletion and restore account, false to sign out
	 */
	const handleDeletionRecovery = useCallback(async (restore: boolean) => {
		if (!deletionRecovery) return;

		if (restore) {
			try {
				const { data: profile } = await supabase
					.from("profiles")
					.select("pre_deletion_data")
					.eq("id", deletionRecovery.userId)
					.single();

				if (profile?.pre_deletion_data) {
					const backup = profile.pre_deletion_data as Record<string, unknown>;

					// Check if original username is still available
					let restoredUsername = backup.username as string | null;
					if (restoredUsername) {
						const { data: existing } = await supabase
							.from("profiles")
							.select("id")
							.eq("username", restoredUsername)
							.neq("id", deletionRecovery.userId)
							.limit(1)
							.single();
						if (existing) restoredUsername = null;
					}

					await supabase
						.from("profiles")
						.update({
							deleted_at: null,
							scheduled_deletion_at: null,
							pre_deletion_data: null,
							...(restoredUsername && { username: restoredUsername }),
							full_name: (backup.fullName as string) ?? null,
							first_name: (backup.firstName as string) ?? null,
							last_name: (backup.lastName as string) ?? null,
							google_avatar_url: (backup.googleAvatarUrl as string) ?? null,
							manual_avatar_url: (backup.manualAvatarUrl as string) ?? null,
							avatar_source: (backup.avatarSource as string) ?? null,
							avatar_url: (backup.avatarUrl as string) ?? null,
							website_url: (backup.websiteUrl as string) ?? null,
							is_private: (backup.isPrivate as boolean) ?? false,
							location_preference: (backup.locationPreference as string) ?? null,
							updated_at: new Date().toISOString(),
						})
						.eq("id", deletionRecovery.userId);
				}
				setDeletionRecovery(null);
				// Refresh to pick up restored profile data
				router.refresh();
			} catch (err) {
				console.error("Failed to restore deleted account:", err);
				// Don't dismiss modal on error — user can retry or sign out
				throw err;
			}
		} else {
			// User chose not to restore — sign them out
			setDeletionRecovery(null);
			await signOut();
		}
	}, [deletionRecovery, supabase, signOut, router]);

	const value = {
		supabase,
		user,
		session,
		isLoading,
		signOut,
		deletionRecovery,
		handleDeletionRecovery,
	};

	return (
		<SupabaseContext.Provider value={value}>
			{children}
		</SupabaseContext.Provider>
	);
}

export const useSupabase = () => {
	const context = useContext(SupabaseContext);
	if (context === null) {
		// Check if environment variables are missing
		const hasUrl = !!process.env.NEXT_PUBLIC_SUPABASE_URL;
		const hasKey = !!(process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY);

		if (!hasUrl || !hasKey) {
			console.error("Missing Supabase environment variables:", {
				url: hasUrl ? "✓" : "✗",
				publishableKey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ? "✓" : "✗",
				anonKey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ? "✓" : "✗"
			});
			throw new Error("Supabase client could not be initialized. Missing environment variables.");
		}

		throw new Error("useSupabase must be used within a SupabaseProvider");
	}
	return context;
};
